import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiError, humanizeServiceError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { StatusBadge } from "../components/StatusBadge";
import { ResultCatastoPF } from "../components/result/ResultCatastoPF";
import { ResultCatastoPG } from "../components/result/ResultCatastoPG";
import { ResultVisuraCamerale } from "../components/result/ResultVisuraCamerale";
import { ResultBilancio } from "../components/result/ResultBilancio";
import { ResultCatastoIndirizzo } from "../components/result/ResultCatastoIndirizzo";
import { ResultCompany } from "../components/result/ResultCompany";
import { ResultVies } from "../components/result/ResultVies";
import { ResultVisuraCatastaleUfficiale } from "../components/result/ResultVisuraCatastaleUfficiale";
import { PROVINCE_DISPONIBILI } from "../lib/provinces";
import { COMUNI_BY_PROVINCIA } from "../lib/comuni-istat";
import {
  normalizePivaCf,
  looksLikePersonaFisicaCF,
  isPartitaIvaItaliana,
  ERR_NO_PERSONA_FISICA,
  ERR_PIVA_FORMAT,
} from "../lib/validators";
import {
  ArrowLeft, Clock, RefreshCw, Hourglass, Download, Loader2, Search, MapPin, CheckCircle2, FileSignature, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_BY_SLUG = {
  vies:                { name: "Verifica VIES (UE)",                  category: "Verifica P.IVA" },
  company:             { name: "Cerca azienda",                       category: "Cerca Azienda" },
  visure:              { name: "Visure camerali",                     category: "Visure Camerali" },
  catasto:             { name: "Catasto — Ricerca rapida",            category: "Visure Catastali" },
  "catasto-documenti": { name: "Catasto — Documenti ufficiali",       category: "Documenti Catastali Ufficiali" },
  bilancio:            { name: "Bilancio Aziendale",                  category: "Bilancio Aziendale" },
};

const ResultCard = ({ result, onRefresh, submitting, onDownloadPdf }) => {
  const isAsyncPending = result.status === "pending_external";
  return (
    <Card className="bg-surface border border-border rounded-xl shadow-sm p-7 animate-fade-in" data-testid="result-card">
      <div className="flex items-start justify-between mb-5 gap-4">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-text-muted mb-2">Risultato</div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={result.status} cacheHit={result.cache_hit} />
            {result.cache_hit && (
              <span className="text-xs text-text-secondary flex items-center gap-1.5">
                <Clock size={12} />
                Da cache · {new Date(result.cached_from || result.created_at).toLocaleString("it-IT")}
              </span>
            )}
            {isAsyncPending && (
              <span className="text-xs text-warning-strong flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" />
                {result.openapi_state || "In lavorazione"}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {result.cache_hit && result.status === "completed" && onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={submitting}
              data-testid="refresh-result" className="rounded-md border-border">
              <RefreshCw size={14} className="mr-1.5" /> Aggiorna
            </Button>
          )}
          {result.status === "completed" && result.pdf_url && (
            <Button size="sm" onClick={onDownloadPdf}
              data-testid="download-pdf-button"
              className="rounded-md bg-brand hover:bg-brand-hover text-white">
              <Download size={14} className="mr-1.5" /> Scarica PDF
            </Button>
          )}
        </div>
      </div>

      {result.status === "failed" ? (
        <div className="text-sm text-danger-strong bg-danger-subtle border border-danger-subtle rounded-md p-4 leading-relaxed">
          {humanizeServiceError(result.error_message, result.service_id) || result.error_message || "Richiesta fallita."}
        </div>
      ) : isAsyncPending ? (
        <div className="bg-warning-subtle/50 border border-warning-subtle rounded-md p-5 flex items-start gap-3">
          <Loader2 size={18} className="text-warning-strong animate-spin flex-shrink-0 mt-0.5" />
          <div className="text-sm text-warning-strong leading-relaxed">
            <div className="font-medium">Richiesta inoltrata a OpenAPI.it</div>
            <p className="mt-1">
              Stato corrente: <span className="font-mono">{result.openapi_state || "—"}</span>.
              Aggiornamento automatico ogni 10 secondi. Il PDF/JSON sarà disponibile non appena evaso.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-subtle border border-border rounded-md p-6 text-sm text-text-secondary text-center" data-testid="result-fallback">
          Risultato non disponibile per questo tipo di richiesta.
        </div>
      )}

      {result.status === "completed" && result.pdf_filename && (
        <div className="mt-4 text-xs text-text-secondary">
          Allegato: <span className="font-mono text-text-primary">{result.pdf_filename}</span>
          {result.pdf_size_bytes && (
            <span className="ml-2">· {(result.pdf_size_bytes / 1024).toFixed(0)} KB</span>
          )}
        </div>
      )}
    </Card>
  );
};

const ComingSoonBanner = () => (
  <div className="bg-warning-subtle border border-warning-subtle rounded-xl p-5 flex items-start gap-3" data-testid="coming-soon-banner">
    <Hourglass size={20} className="text-warning-strong flex-shrink-0 mt-0.5" />
    <div>
      <div className="text-sm font-medium text-warning-strong">In arrivo nella prossima release</div>
      <p className="text-sm text-warning-strong mt-1 leading-relaxed">
        Questo servizio richiede gestione asincrona ed è in fase di implementazione.
      </p>
    </div>
  </div>
);

export default function SearchCategory() {
  const { category_slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const meta = CATEGORY_BY_SLUG[category_slug];

  const [allServices, setAllServices] = useState([]);
  const [values, setValues] = useState({});
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [catTab, setCatTab] = useState("piva"); // piva | cf | indirizzo
  const [catDocTab, setCatDocTab] = useState("immobile"); // immobile | soggetto_pg | soggetto_pf
  const [catDocSubTab, setCatDocSubTab] = useState("indirizzo"); // per "immobile": indirizzo | dati_catastali
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Catasto Indirizzo 2-step state
  const [indirizzoQuery, setIndirizzoQuery] = useState("");
  const [indirizzoComune, setIndirizzoComune] = useState("");
  const [indirizzoProvincia, setIndirizzoProvincia] = useState("");
  const [indirizzoLoading, setIndirizzoLoading] = useState(false);
  const [indirizzoOptions, setIndirizzoOptions] = useState(null); // null|[]|[items]
  const [indirizzoEsito, setIndirizzoEsito] = useState(null);
  const [selectedIndirizzo, setSelectedIndirizzo] = useState(null);

  const pollTimerRef = useRef(null);
  const wasPendingRef = useRef(false);

  useEffect(() => {
    api.get("/services").then(({ data }) => setAllServices(data));
  }, []);

  const categoryServices = useMemo(
    () => allServices.filter((s) => s.category === meta?.category),
    [allServices, meta]
  );

  useEffect(() => {
    if (categoryServices.length === 0) return;
    if (category_slug === "vies") {
      setSelectedServiceId("vies_check");
    } else if (category_slug === "company") {
      setSelectedServiceId(selectedServiceId === "company_it_advanced" ? "company_it_advanced" : "company_it_start");
    } else if (category_slug === "visure") {
      const visureIds = ["visura_ord_societa_capitali", "visura_ord_societa_persone", "visura_ord_impresa_individuale"];
      if (!selectedServiceId || !visureIds.includes(selectedServiceId)) {
        setSelectedServiceId("visura_ord_societa_capitali");
      }
    } else if (category_slug === "bilancio") {
      setSelectedServiceId("bilancio_ottico");
    } else if (category_slug === "catasto") {
      setSelectedServiceId(
        catTab === "indirizzo" ? "catasto_ricerca_indirizzo" :
        catTab === "cf" ? "catasto_ricerca_persona_fisica" :
        "catasto_ricerca_persona_giuridica"
      );
    } else if (category_slug === "catasto-documenti") {
      setSelectedServiceId(
        catDocTab === "soggetto_pg" ? "visura_catastale_soggetto_pg_pdf" :
        catDocTab === "soggetto_pf" ? "visura_catastale_soggetto_pf_pdf" :
        catDocTab === "planimetria" ? "planimetria_catastale_pdf" :
        "visura_catastale_immobile_pdf"
      );
    }
    // eslint-disable-next-line
  }, [category_slug, categoryServices.length, catTab, catDocTab]);

  const currentService = useMemo(
    () => categoryServices.find((s) => s.service_id === selectedServiceId),
    [categoryServices, selectedServiceId]
  );

  useEffect(() => {
    if (!currentService) return;
    // Preserva i valori esistenti per le chiavi richieste dal nuovo service.
    // Le 3 visure condividono tutte cf_piva_id → P.IVA digitata resta al cambio tipo.
    setValues((prev) => {
      const next = {};
      for (const p of currentService.required_params || []) {
        next[p.key] = (prev && prev[p.key]) || "";
      }
      return next;
    });
    // Cleanup esplicito di errore/risultato precedenti — niente stati appiccicosi
    setResult(null);
    setError("");
    setIndirizzoOptions(null);
    setSelectedIndirizzo(null);
    setIndirizzoEsito(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentService?.service_id]);

  // Polling
  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (result?.status === "pending_external") {
      wasPendingRef.current = true;
      pollTimerRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/requests/${result.id}`);
          setResult(data);
          if (data.status !== "pending_external") {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            if (data.status === "completed" && wasPendingRef.current) {
              toast.success("Risultato pronto", { duration: 6000 });
              wasPendingRef.current = false;
            } else if (data.status === "failed" && wasPendingRef.current) {
              const friendly = humanizeServiceError(data.error_message, data.service_id);
              toast.error(`Richiesta fallita: ${friendly || data.error_message || ""}`);
              wasPendingRef.current = false;
            }
          }
        } catch (e) { /* swallow */ }
      }, 10000);
    }
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [result?.status, result?.id]);

  if (!meta) return <div className="text-sm text-text-muted">Categoria non trovata.</div>;

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");
    // Catasto indirizzo richiede un indirizzo selezionato (id_indirizzo valorizzato)
    if (category_slug === "catasto" && catTab === "indirizzo" && !values.id_indirizzo) {
      setError("Seleziona prima un indirizzo dalla lista risultati.");
      return;
    }
    // Bilancio: blocca CF persona fisica (16 char alfanumerici) prima della chiamata API
    if (selectedServiceId === "bilancio_ottico") {
      const v = normalizePivaCf(values.cf_piva_id);
      if (!v) {
        setError("Inserisci la Partita IVA dell'azienda.");
        return;
      }
      if (looksLikePersonaFisicaCF(v)) {
        setError(ERR_NO_PERSONA_FISICA);
        return;
      }
      if (!isPartitaIvaItaliana(v)) {
        setError(ERR_PIVA_FORMAT);
        return;
      }
    }
    setConfirmOpen(true);
  };

  const doSubmit = async (forceRefresh = false) => {
    setSubmitting(true);
    setError("");
    setResult(null);  // pulisce il risultato precedente PRIMA della nuova chiamata
    try {
      const { data } = await api.post("/requests", {
        service_id: selectedServiceId,
        params: values,
        force_refresh: forceRefresh,
      });
      setResult(data);
      setConfirmOpen(false);
      if (data.status === "pending_external") {
        toast.info("Richiesta inoltrata - aggiornamento automatico in corso", { duration: 5000 });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(humanizeServiceError(detail, selectedServiceId) || formatApiError(detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onDownloadPdf = async () => {
    if (!result?.id) return;
    const token = localStorage.getItem("wattline_token");
    const res = await fetch(`${API}/requests/${result.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Errore download PDF"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = result.pdf_filename || `visura_${result.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const searchIndirizzo = async () => {
    setIndirizzoOptions(null);
    setSelectedIndirizzo(null);
    setIndirizzoEsito(null);
    setValues({ ...values, id_indirizzo: "" });
    if ((indirizzoQuery || "").trim().length < 3) {
      toast.error("Indirizzo: almeno 3 caratteri");
      return;
    }
    if ((indirizzoComune || "").trim().length < 2) {
      toast.error("Comune obbligatorio");
      return;
    }
    const prov = (indirizzoProvincia || "").trim().toUpperCase();
    if (prov.length !== 2 || !/^[A-Z]{2}$/.test(prov)) {
      toast.error("Provincia: sigla a 2 lettere (es. MI, RM, PT)");
      return;
    }
    setIndirizzoLoading(true);
    try {
      const { data } = await api.post("/catasto/search-indirizzo", {
        indirizzo: indirizzoQuery, comune: indirizzoComune, provincia: prov,
      });
      // Backend ritorna shape: {risultato: [...], esito, id, parametri}
      const list = Array.isArray(data?.risultato) ? data.risultato : [];
      setIndirizzoOptions(list);
      setIndirizzoEsito(data?.esito || null);
      if (list.length === 0) toast.info(data?.esito || "Nessun indirizzo trovato");
    } catch (err) {
      setIndirizzoOptions([]);
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore nella ricerca");
    } finally {
      setIndirizzoLoading(false);
    }
  };

  const pickIndirizzo = (opt) => {
    // Heuristica: l'id_indirizzo è sotto opt.id, opt.id_indirizzo o opt.codice
    const id = opt?.id_indirizzo || opt?.id || opt?.codice || opt?.uuid;
    const label = opt?.indirizzo_completo || opt?.indirizzo || opt?.descrizione || JSON.stringify(opt);
    setSelectedIndirizzo({ id, label });
    setValues({ ...values, id_indirizzo: id });
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <button onClick={() => navigate("/services")} data-testid="back-to-catalog"
        className="text-sm text-text-secondary hover:text-text-primary flex items-center gap-2 transition-colors">
        <ArrowLeft size={16} /> Torna al catalogo
      </button>

      <div>
        <div className="text-[11px] font-medium tracking-widest uppercase text-text-muted mb-2">{meta.category}</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-text-primary">{meta.name}</h1>
        {category_slug === "visure" && (
          <p className="text-base text-text-secondary mt-3 max-w-2xl leading-relaxed">
            Le visure camerali sono richieste asincrone: la richiesta viene inoltrata e il PDF
            sarà scaricabile non appena disponibile (in genere pochi minuti).
          </p>
        )}
        {category_slug === "catasto" && (
          <p className="text-base text-text-secondary mt-3 max-w-2xl leading-relaxed">
            <strong>Ricerca rapida</strong> per pre-screening commerciale. Dati strutturati JSON,
            costo ridotto. Per documenti probatori firmati AdE usa <em>Catasto — Documenti ufficiali</em>.
          </p>
        )}
        {category_slug === "catasto-documenti" && (
          <p className="text-base text-text-secondary mt-3 max-w-2xl leading-relaxed">
            <strong>PDF firmati digitalmente Agenzia delle Entrate</strong> per dossier contrattuali e
            KYC. Province TN/BZ/TS/GO escluse (catasto autonomo o sistema tavolare).
          </p>
        )}
        {category_slug === "bilancio" && (
          <p className="text-base text-text-secondary mt-3 max-w-2xl leading-relaxed">
            <strong>Bilancio ufficiale depositato</strong> presso la Camera di Commercio. Disponibile
            solo per società di capitale (SRL, SPA, SAPA). Riceverai PDF del bilancio, verbale
            assemblea soci e dati strutturati XBRL.
          </p>
        )}
      </div>

      {/* Tab switcher per catasto */}
      {category_slug === "catasto" && (
        <div className="inline-flex bg-subtle border border-border rounded-md p-1" data-testid="catasto-tabs">
          {[
            { v: "piva", label: "P.IVA (Persona Giuridica)" },
            { v: "cf", label: "C.F. (Persona Fisica)" },
            { v: "indirizzo", label: "Per Indirizzo" },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={`catasto-tab-${t.v}`}
              onClick={() => setCatTab(t.v)}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                catTab === t.v
                  ? "bg-surface text-text-primary shadow-sm"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab switcher per catasto-documenti (PDF ufficiale AdE) */}
      {category_slug === "catasto-documenti" && (
        <div className="inline-flex bg-subtle border border-border rounded-md p-1" data-testid="catasto-doc-tabs">
          {[
            { v: "immobile", label: "Immobile" },
            { v: "soggetto_pg", label: "Soggetto - P. Giuridica" },
            { v: "soggetto_pf", label: "Soggetto - P. Fisica" },
            { v: "planimetria", label: "Elaborato planimetrico" },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={`catdoc-tab-${t.v}`}
              type="button"
              onClick={() => setCatDocTab(t.v)}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                catDocTab === t.v
                  ? "bg-surface text-text-primary shadow-sm"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-7">
        <form onSubmit={onSubmit} className="space-y-5" data-testid="service-form">

          {category_slug === "vies" && <FormFields service={currentService} values={values} setValues={setValues} />}

          {category_slug === "company" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-text-secondary">Tipologia dati</Label>
                <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                  <SelectTrigger data-testid="company-data-level" className="h-11 rounded-md border-border bg-surface">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company_it_start">Dati base (€ 0,30)</SelectItem>
                    <SelectItem value="company_it_advanced">Dati avanzati (€ 0,80)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormFields service={currentService} values={values} setValues={setValues} />
            </>
          )}

          {category_slug === "visure" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-text-secondary">Tipologia visura</Label>
                <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                  <SelectTrigger data-testid="visure-type" className="h-11 rounded-md border-border bg-surface">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visura_ord_societa_capitali">Società di capitali (S.r.l., S.p.A.)</SelectItem>
                    <SelectItem value="visura_ord_societa_persone">Società di persone (S.n.c., S.a.s.)</SelectItem>
                    <SelectItem value="visura_ord_impresa_individuale">Impresa individuale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormFields service={currentService} values={values} setValues={setValues} />
            </>
          )}

          {category_slug === "bilancio" && (
            <BilancioForm values={values} setValues={setValues} />
          )}

          {category_slug === "catasto" && catTab !== "indirizzo" && (
            <FormFields service={currentService} values={values} setValues={setValues} />
          )}

          {category_slug === "catasto" && catTab === "indirizzo" && (
            <CatastoIndirizzoStep1
              query={indirizzoQuery} setQuery={setIndirizzoQuery}
              comune={indirizzoComune} setComune={setIndirizzoComune}
              provincia={indirizzoProvincia} setProvincia={setIndirizzoProvincia}
              onSearch={searchIndirizzo} loading={indirizzoLoading}
              options={indirizzoOptions}
              esito={indirizzoEsito}
              selected={selectedIndirizzo}
              onPick={pickIndirizzo}
            />
          )}

          {category_slug === "catasto-documenti" && (
            <CatastoDocForm
              tab={catDocTab}
              subTab={catDocSubTab}
              setSubTab={setCatDocSubTab}
              values={values}
              setValues={setValues}
            />
          )}

          {error && (
            <div data-testid="service-error" className="text-sm text-danger-strong bg-danger-subtle border border-danger-subtle px-3 py-2 rounded-md">
              {error}
            </div>
          )}

          <Button type="submit" data-testid="service-submit" disabled={submitting}
            className="rounded-md bg-accent hover:bg-accent-strong text-white h-11 px-6 font-medium">
            {submitting ? "Invio…" : "Esegui verifica"}
          </Button>
        </form>
      </Card>

      {result && result.status === "completed" && result.service_id === "catasto_ricerca_persona_fisica" ? (
        <ResultCatastoPF request={result} onNewSearch={() => { setResult(null); setValues({}); }} />
      ) : result && result.status === "completed" && result.service_id === "catasto_ricerca_persona_giuridica" ? (
        <ResultCatastoPG request={result} onNewSearch={() => { setResult(null); setValues({}); }} />
      ) : result && result.service_id === "catasto_ricerca_indirizzo" ? (
        <ResultCatastoIndirizzo
          request={result}
          onNewSearch={() => { setResult(null); setValues({}); setSelectedIndirizzo(null); setIndirizzoOptions(null); setIndirizzoQuery(""); setIndirizzoComune(""); setIndirizzoProvincia(""); setIndirizzoEsito(null); }}
          onConfirmSelection={(sel) => {
            // Step C (dettaglio singolo immobile) non ancora cablato al backend.
            // I dati base (categoria, classamento, rendita, foglio/particella/sub)
            // sono GIA' visibili nella lista immobili soprastante.
            console.log("[catasto-indirizzo] selezione utente:", sel);
            toast.info("Dettaglio singolo immobile in arrivo. Per ora i dati base sono già visibili nella lista qui sopra.");
          }}
          onManualRefresh={async () => {
            try { const { data } = await api.get(`/requests/${result.id}`); setResult(data); } catch { /* noop */ }
          }}
        />
      ) : result && ["visura_ord_societa_capitali","visura_ord_societa_persone","visura_ord_impresa_individuale"].includes(result.service_id) ? (
        <ResultVisuraCamerale request={result} onUpdate={setResult} onNewSearch={() => { setResult(null); setValues({}); }} />
      ) : result && result.service_id === "bilancio_ottico" ? (
        <ResultBilancio request={result} onUpdate={setResult} onNewSearch={() => { setResult(null); setValues({}); }} />
      ) : result && ["visura_catastale_immobile_pdf","visura_catastale_soggetto_pg_pdf","visura_catastale_soggetto_pf_pdf","planimetria_catastale_pdf"].includes(result.service_id) ? (
        <ResultVisuraCatastaleUfficiale request={result} onUpdate={setResult} onNewSearch={() => { setResult(null); setValues({}); }} />
      ) : result && result.status === "completed" && (result.service_id === "company_it_start" || result.service_id === "company_it_advanced") ? (
        <ResultCompany request={result} onNewSearch={() => { setResult(null); setValues({}); }} />
      ) : result && result.status === "completed" && result.service_id === "vies_check" ? (
        <ResultVies
          request={result}
          onNewSearch={() => { setResult(null); setValues({}); }}
          onRetry={() => { /* mantiene values, l'utente clicca submit */ }}
        />
      ) : result && (
        <ResultCard result={result} submitting={submitting}
          onRefresh={result.cache_hit ? () => doSubmit(true) : undefined}
          onDownloadPdf={onDownloadPdf} />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Conferma richiesta"
        description={currentService
          ? `Stai per richiedere "${currentService.name}". La richiesta verrà registrata e processata.`
          : ""}
        params={values}
        costEur={user?.role === "admin" && currentService && typeof currentService.cost_cents === "number" ? currentService.cost_cents / 100 : undefined}
        loading={submitting}
        onConfirm={() => doSubmit(false)}
      />
    </div>
  );
}

const FormFields = ({ service, values, setValues }) => (
  <div className="space-y-5">
    {(service?.required_params || []).map((p) => (
      <div key={p.key} className="space-y-1.5">
        <Label className="text-xs font-medium text-text-secondary">{p.label}</Label>
        <Input
          type={p.type || "text"}
          data-testid={`param-${p.key}`}
          value={values[p.key] || ""}
          onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
          placeholder={p.placeholder}
          required
          className="h-11 rounded-md border-border bg-surface focus-visible:ring-accent/30 focus-visible:border-accent"
        />
      </div>
    ))}
  </div>
);

const BILANCIO_ANNI = ["ultimo", "2024", "2023", "2022", "2021", "2020"];

const BilancioForm = ({ values, setValues }) => {
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const piva = normalizePivaCf(values.cf_piva_id);
  const looksLikeCF = looksLikePersonaFisicaCF(piva);

  useEffect(() => {
    if (!values.anno_chiusura) setValues((s) => ({ ...s, anno_chiusura: "ultimo", ...s }));
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-4" data-testid="bilancio-form">
      <div className="rounded-md bg-subtle border border-border p-4 text-xs text-text-secondary leading-relaxed">
        <strong className="text-text-primary">Bilancio Aziendale ufficiale.</strong> Richiede la P.IVA di una società di
        capitale (SRL, SPA, SAPA). Riceverai PDF del bilancio, PDF del verbale assemblea soci e XBRL strutturato.
        Tempi di evasione: fino a 15 minuti.
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-text-secondary">Partita IVA azienda *</Label>
        <Input
          type="text"
          data-testid="param-cf_piva_id"
          value={values.cf_piva_id || ""}
          onChange={(e) => set("cf_piva_id", normalizePivaCf(e.target.value))}
          placeholder="12345678901"
          maxLength={16}
          required
          className="h-11 rounded-md border-border bg-surface uppercase tabular-nums focus-visible:ring-accent/30 focus-visible:border-accent"
        />
        {looksLikeCF && (
          <p className="text-xs text-danger-strong flex items-start gap-1.5 mt-1.5">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              Il servizio Bilancio è disponibile <strong>solo per società di capitale</strong> (SRL, SPA, SAPA).
              Per persone fisiche o ditte individuali questo servizio non è applicabile.
            </span>
          </p>
        )}
        {!looksLikeCF && piva && piva.length > 0 && piva.length !== 11 && (
          <p className="text-xs text-text-muted">
            La Partita IVA italiana è composta da 11 cifre numeriche ({piva.length}/11).
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-text-secondary">Anno chiusura bilancio</Label>
        <Select value={values.anno_chiusura || "ultimo"} onValueChange={(v) => set("anno_chiusura", v)}>
          <SelectTrigger data-testid="param-anno_chiusura" className="h-11 rounded-md border-border bg-surface">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BILANCIO_ANNI.map((a) => (
              <SelectItem key={a} value={a}>{a === "ultimo" ? "Ultimo disponibile" : a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-text-muted">
          Lascia &quot;Ultimo disponibile&quot; per ricevere l&apos;ultimo bilancio depositato. Per bilanci storici scegli l&apos;anno specifico.
        </p>
      </div>
    </div>
  );
};

const CatastoIndirizzoStep1 = ({ query, setQuery, comune, setComune, provincia, setProvincia, onSearch, loading, options, esito, selected, onPick }) => (
  <div className="space-y-4">
    <div className="rounded-md bg-subtle border border-border p-4 text-xs text-text-secondary leading-relaxed">
      <strong className="text-text-primary">Flusso in 2 passi.</strong> Prima cerca l'indirizzo (gratuito), poi seleziona un risultato e conferma per generare la visura catastale (a pagamento).
    </div>

    <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_120px] gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-text-secondary">Indirizzo</Label>
        <Input
          type="text"
          data-testid="param-indirizzo-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Es. Via Roma 10"
          className="h-11 rounded-md border-border bg-surface focus-visible:ring-accent/30 focus-visible:border-accent"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-text-secondary">Comune</Label>
        <Input
          type="text"
          data-testid="param-indirizzo-comune"
          value={comune}
          onChange={(e) => setComune(e.target.value)}
          placeholder="Es. Milano"
          className="h-11 rounded-md border-border bg-surface focus-visible:ring-accent/30 focus-visible:border-accent"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-text-secondary">Provincia</Label>
        <Input
          type="text"
          data-testid="param-indirizzo-provincia"
          value={provincia}
          onChange={(e) => setProvincia(e.target.value.toUpperCase().slice(0, 2))}
          placeholder="MI"
          maxLength={2}
          className="h-11 rounded-md border-border bg-surface uppercase tabular-nums focus-visible:ring-accent/30 focus-visible:border-accent"
        />
      </div>
    </div>

    <Button
      type="button"
      onClick={onSearch}
      disabled={loading}
      data-testid="catasto-search-indirizzo-btn"
      variant="outline"
      className="h-10 px-4 rounded-md border-border"
    >
      {loading
        ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Ricerca in corso…</>
        : <><Search size={14} className="mr-1.5" /> Cerca indirizzo</>}
    </Button>

    {options !== null && (
      <div className="border border-border rounded-md overflow-hidden" data-testid="indirizzo-results">
        <div className="px-4 py-2.5 bg-subtle border-b border-border text-xs font-medium text-text-secondary flex items-center gap-2">
          <MapPin size={13} />
          {options.length} {options.length === 1 ? "risultato" : "risultati"}
        </div>
        {options.length === 0 ? (
          <div className="px-4 py-6 text-sm text-text-muted text-center">
            {esito || "Nessun indirizzo trovato"}.
            <div className="mt-1.5 text-xs">Verifica nome via, comune e sigla provincia. Es. &quot;Via Roma 10&quot; · &quot;Milano&quot; · &quot;MI&quot;.</div>
          </div>
        ) : (
          <div className="divide-y divide-border max-h-72 overflow-y-auto">
            {options.map((opt, i) => {
              const id = opt?.id_indirizzo || opt?.id || opt?.codice || opt?.uuid || i;
              const label = opt?.indirizzo_completo || opt?.indirizzo || opt?.descrizione || JSON.stringify(opt);
              const isSelected = selected?.id === id;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`indirizzo-option-${i}`}
                  onClick={() => onPick(opt)}
                  className={`w-full text-left px-4 py-3 text-sm flex items-start gap-3 transition-colors ${
                    isSelected ? "bg-accent-subtle" : "hover:bg-subtle"
                  }`}
                >
                  <div className={`mt-0.5 ${isSelected ? "text-accent-strong" : "text-text-muted"}`}>
                    {isSelected ? <CheckCircle2 size={15} /> : <MapPin size={15} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-text-primary">{label}</div>
                    {opt?.comune && (
                      <div className="text-xs text-text-muted mt-0.5">
                        {opt.comune}{opt.provincia ? ` (${opt.provincia})` : ""}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    )}

    {selected && (
      <div className="text-xs text-success-strong bg-success-subtle border border-success-subtle px-3 py-2 rounded-md flex items-center gap-2" data-testid="indirizzo-selected">
        <CheckCircle2 size={13} /> Indirizzo selezionato: <span className="font-medium">{selected.label}</span>
      </div>
    )}
  </div>
);


/* ====================== CATASTO DOCUMENTI UFFICIALI - Form ====================== */

const ProvinciaSelect = ({ value, onChange, testIdSuffix = "" }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-text-secondary">Provincia *</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={`catdoc-provincia${testIdSuffix}`}
                     className="h-11 rounded-md border-border bg-surface">
        <SelectValue placeholder="Seleziona provincia…" />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {PROVINCE_DISPONIBILI.map((p) => (
          <SelectItem key={p.sigla} value={p.sigla}>
            {p.sigla} · {p.nome} <span className="text-text-muted text-xs">({p.regione})</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const ComuneSelect = ({ provincia, value, onChange, testIdSuffix = "" }) => {
  const comuni = useMemo(() => (provincia ? COMUNI_BY_PROVINCIA[provincia] || [] : []), [provincia]);
  const disabled = !provincia;
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!query) return comuni;
    const q = query.toUpperCase();
    return comuni.filter((c) => c.includes(q));
  }, [query, comuni]);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-text-secondary">Comune *</Label>
      <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid={`catdoc-comune${testIdSuffix}`}
                       className="h-11 rounded-md border-border bg-surface">
          <SelectValue placeholder={disabled ? "Seleziona prima la provincia" : `Cerca tra ${comuni.length} comuni…`} />
        </SelectTrigger>
        <SelectContent className="max-h-80">
          <div className="px-2 py-1.5 sticky top-0 bg-surface border-b border-border">
            <Input
              autoFocus
              placeholder="Digita per filtrare…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 text-sm"
              data-testid={`catdoc-comune-search${testIdSuffix}`}
            />
          </div>
          {filtered.slice(0, 200).map((c) => (
            <SelectItem key={c} value={c}>{c}</SelectItem>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-text-muted">Nessun comune corrispondente</div>
          )}
          {filtered.length > 200 && (
            <div className="px-3 py-2 text-xs text-text-muted border-t border-border">
              Mostrati primi 200 di {filtered.length}. Affina la ricerca.
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

const TipoCatastoRadio = ({ value, onChange }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-text-secondary">Tipo catasto *</Label>
    <div className="inline-flex bg-subtle border border-border rounded-md p-1" data-testid="catdoc-tipo-catasto">
      {[{ v: "F", label: "Fabbricati" }, { v: "T", label: "Terreni" }].map((o) => (
        <button
          key={o.v} type="button" onClick={() => onChange(o.v)}
          data-testid={`catdoc-tipo-cat-${o.v}`}
          className={`px-3 py-1.5 text-sm rounded transition-colors ${
            value === o.v ? "bg-surface text-text-primary shadow-sm font-medium" : "text-text-secondary hover:text-text-primary"
          }`}
        >{o.label}</button>
      ))}
    </div>
  </div>
);

const TipoVisuraSelect = ({ value, onChange }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-text-secondary">Tipo visura *</Label>
    <Select value={value || "ordinaria"} onValueChange={onChange}>
      <SelectTrigger data-testid="catdoc-tipo-visura"
                     className="h-11 rounded-md border-border bg-surface">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ordinaria">Ordinaria (stato attuale)</SelectItem>
        <SelectItem value="storica">Storica (timeline immobile)</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const CatastoDocForm = ({ tab, subTab, setSubTab, values, setValues }) => {
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  // Default (non necessari per la planimetria: schema senza tipo_catasto/tipo_visura)
  useEffect(() => {
    if (tab === "planimetria") return;
    setValues((s) => ({
      tipo_catasto: s.tipo_catasto || "F",
      tipo_visura: s.tipo_visura || "ordinaria",
      ...s,
    }));
    // eslint-disable-next-line
  }, [tab]);

  if (tab === "planimetria") {
    return (
      <div className="space-y-4" data-testid="catdoc-form-planimetria">
        <div className="rounded-md bg-subtle border border-border p-4 text-xs text-text-secondary leading-relaxed">
          <strong className="text-text-primary">Elaborato planimetrico.</strong>{" "}
          Schema ufficiale dei subalterni della particella (piani e unità), estratto dagli archivi
          dell'Agenzia delle Entrate. Non è la planimetria interna delle singole unità immobiliari,
          che è un documento ad accesso ristretto richiedibile solo dal titolare o da un tecnico delegato.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProvinciaSelect value={values.provincia} onChange={(v) => { set("provincia", v); set("comune", ""); }} testIdSuffix="-plan" />
          <ComuneSelect provincia={values.provincia} value={values.comune} onChange={(v) => set("comune", v)} testIdSuffix="-plan" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Foglio *</Label>
            <Input type="text" data-testid="catdoc-plan-foglio"
                   value={values.foglio || ""}
                   onChange={(e) => set("foglio", e.target.value)}
                   placeholder="123" required
                   className="h-11 rounded-md border-border bg-surface tabular-nums" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Particella *</Label>
            <Input type="text" data-testid="catdoc-plan-particella"
                   value={values.particella || ""}
                   onChange={(e) => set("particella", e.target.value)}
                   placeholder="456" required
                   className="h-11 rounded-md border-border bg-surface tabular-nums" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Sezione</Label>
            <Input type="text" data-testid="catdoc-plan-sezione"
                   value={values.sezione || ""}
                   onChange={(e) => set("sezione", e.target.value.toUpperCase())}
                   placeholder="A" className="h-11 rounded-md border-border bg-surface uppercase" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Sezione urbana</Label>
            <Input type="text" data-testid="catdoc-plan-sezione-urbana"
                   value={values.sezione_urbana || ""}
                   onChange={(e) => set("sezione_urbana", e.target.value.toUpperCase())}
                   placeholder="—" className="h-11 rounded-md border-border bg-surface uppercase" />
          </div>
        </div>
      </div>
    );
  }

  if (tab === "soggetto_pg") {
    return (
      <div className="space-y-4" data-testid="catdoc-form-soggetto-pg">
        <div className="rounded-md bg-subtle border border-border p-4 text-xs text-text-secondary leading-relaxed">
          <strong className="text-text-primary">Visura catastale soggetto PG.</strong> Restituisce l'elenco immobili intestati a una società in una provincia specifica. PDF firmato AdE.
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-text-secondary">Codice Fiscale o Partita IVA *</Label>
          <Input
            type="text"
            data-testid="catdoc-cf-piva"
            value={values.cf_piva || ""}
            onChange={(e) => set("cf_piva", e.target.value.toUpperCase())}
            placeholder="12345678901 oppure RSSMRA80A01F205Z"
            maxLength={16}
            className="h-11 rounded-md border-border bg-surface uppercase tabular-nums"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProvinciaSelect value={values.provincia} onChange={(v) => set("provincia", v)} />
          <TipoCatastoRadio value={values.tipo_catasto || "F"} onChange={(v) => set("tipo_catasto", v)} />
        </div>
        <TipoVisuraSelect value={values.tipo_visura} onChange={(v) => set("tipo_visura", v)} />
      </div>
    );
  }

  if (tab === "soggetto_pf") {
    return (
      <div className="space-y-4" data-testid="catdoc-form-soggetto-pf">
        <div className="rounded-md bg-subtle border border-border p-4 text-xs text-text-secondary leading-relaxed">
          <strong className="text-text-primary">Visura catastale soggetto PF.</strong> Restituisce l'elenco immobili intestati a una persona fisica in una provincia specifica. PDF firmato AdE.
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-text-secondary">Codice Fiscale *</Label>
          <Input
            type="text"
            data-testid="catdoc-cf"
            value={values.cf || ""}
            onChange={(e) => set("cf", e.target.value.toUpperCase())}
            placeholder="RSSMRA80A01F205Z"
            maxLength={16}
            className="h-11 rounded-md border-border bg-surface uppercase tabular-nums"
          />
          {values.cf && values.cf.length !== 16 && (
            <p className="text-xs text-warning-strong flex items-center gap-1.5">
              <AlertTriangle size={11} /> Il CF deve essere di 16 caratteri ({values.cf.length}/16)
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProvinciaSelect value={values.provincia} onChange={(v) => set("provincia", v)} />
          <TipoCatastoRadio value={values.tipo_catasto || "F"} onChange={(v) => set("tipo_catasto", v)} />
        </div>
        <TipoVisuraSelect value={values.tipo_visura} onChange={(v) => set("tipo_visura", v)} />
      </div>
    );
  }

  // tab === "immobile"
  return (
    <div className="space-y-4" data-testid="catdoc-form-immobile">
      <div className="rounded-md bg-subtle border border-border p-4 text-xs text-text-secondary leading-relaxed">
        <strong className="text-text-primary">Visura catastale singolo immobile.</strong> Cerca per indirizzo civico oppure per dati catastali (foglio/particella). PDF firmato AdE.
      </div>

      <div className="inline-flex bg-subtle border border-border rounded-md p-1" data-testid="catdoc-immobile-subtabs">
        {[
          { v: "indirizzo", label: "Da indirizzo", icon: MapPin },
          { v: "dati_catastali", label: "Da dati catastali", icon: FileSignature },
        ].map((t) => (
          <button
            key={t.v} type="button" onClick={() => setSubTab(t.v)}
            data-testid={`catdoc-immobile-subtab-${t.v}`}
            className={`px-3 py-1.5 text-sm font-medium rounded transition-colors inline-flex items-center gap-1.5 ${
              subTab === t.v ? "bg-surface text-text-primary shadow-sm" : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ProvinciaSelect value={values.provincia} onChange={(v) => { set("provincia", v); set("comune", ""); }} />
        <ComuneSelect provincia={values.provincia} value={values.comune} onChange={(v) => set("comune", v)} />
      </div>

      {subTab === "indirizzo" ? (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Indirizzo (via/piazza) *</Label>
            <Input
              type="text"
              data-testid="catdoc-imm-indirizzo"
              value={values.indirizzo || ""}
              onChange={(e) => set("indirizzo", e.target.value.toUpperCase())}
              placeholder="Es. VIA ROMA"
              className="h-11 rounded-md border-border bg-surface uppercase"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Civico *</Label>
            <Input
              type="text"
              data-testid="catdoc-imm-civico"
              value={values.civico || ""}
              onChange={(e) => set("civico", e.target.value)}
              placeholder="42"
              className="h-11 rounded-md border-border bg-surface tabular-nums"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Foglio *</Label>
            <Input type="text" data-testid="catdoc-imm-foglio"
                   value={values.foglio || ""}
                   onChange={(e) => set("foglio", e.target.value)}
                   placeholder="123" className="h-11 rounded-md border-border bg-surface tabular-nums" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Particella *</Label>
            <Input type="text" data-testid="catdoc-imm-particella"
                   value={values.particella || ""}
                   onChange={(e) => set("particella", e.target.value)}
                   placeholder="456" className="h-11 rounded-md border-border bg-surface tabular-nums" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Subalterno</Label>
            <Input type="text" data-testid="catdoc-imm-subalterno"
                   value={values.subalterno || ""}
                   onChange={(e) => set("subalterno", e.target.value)}
                   placeholder="1" className="h-11 rounded-md border-border bg-surface tabular-nums" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Sezione</Label>
            <Input type="text" data-testid="catdoc-imm-sezione"
                   value={values.sezione || ""}
                   onChange={(e) => set("sezione", e.target.value.toUpperCase())}
                   placeholder="A" className="h-11 rounded-md border-border bg-surface uppercase" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TipoCatastoRadio value={values.tipo_catasto || "F"} onChange={(v) => set("tipo_catasto", v)} />
        <TipoVisuraSelect value={values.tipo_visura} onChange={(v) => set("tipo_visura", v)} />
      </div>
    </div>
  );
};
